// One recording, start to transcript — the composer mic's half of voice.
// A call is the other half and lives in the header (shared/_jane_call_dock,
// raised by the `assistant-voice:open` window event); every composer mic
// records through this instead: Jane's (assistant_compose_controller, which
// sends the transcript) and a person thread's (dictation_controller, which
// leaves it in the field to check before it goes to them).
//
//   const recorder = new VoiceRecorder({ onState, onText, onError })
//   await recorder.start()   // onState("recording") once the mic is live
//   recorder.stop()          // onState("transcribing"), then onText(text)
//   recorder.cancel()        // drops the audio, onState("idle")
//
// onError gets a short user-facing line ("Microphone blocked", "Didn't catch
// that", ...) and the recorder is back to idle when it fires.
export default class VoiceRecorder {
    constructor({ onState = () => {}, onText = () => {}, onError = () => {}, url = "/assistant/transcribe" } = {}) {
        this._onState = onState
        this._onText = onText
        this._onError = onError
        this._url = url
        this.state = "idle" // idle | recording | transcribing
        this._recorder = null
        this._stream = null
        this._chunks = []
        this._cancelled = false
    }

    async start() {
        if (this.state !== "idle") return
        if (!navigator.mediaDevices || !window.MediaRecorder) return this._fail("Voice not supported")
        try {
            this._stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
            })
        } catch (_err) {
            return this._fail("Microphone blocked")
        }

        this._chunks = []
        this._cancelled = false
        this._mimeType = this._pickMime()
        this._recorder = new MediaRecorder(this._stream, this._mimeType ? { mimeType: this._mimeType } : undefined)
        this._recorder.ondataavailable = (ev) => {
            if (ev.data && ev.data.size > 0) this._chunks.push(ev.data)
        }
        this._recorder.onstop = () => this._onStop()
        this._recorder.start()
        this._setState("recording")
    }

    stop() {
        if (this._recorder && this._recorder.state !== "inactive") this._recorder.stop()
    }

    cancel() {
        this._cancelled = true
        this.stop()
        this._teardownStream()
        this._setState("idle")
    }

    async _onStop() {
        this._teardownStream()
        if (this._cancelled) {
            this._chunks = []
            return
        }

        const blob = new Blob(this._chunks, { type: this._mimeType || "audio/webm" })
        this._chunks = []
        if (blob.size < 1200) return this._fail("Didn't catch that")

        this._setState("transcribing")
        let text
        try {
            text = await this._transcribe(blob)
        } catch (_err) {
            return this._fail("Transcription failed")
        }
        if (!text || !text.trim()) return this._fail("Didn't catch that")
        this._setState("idle")
        this._onText(text.trim())
    }

    async _transcribe(blob) {
        const token = document.querySelector("meta[name='csrf-token']")?.content
        const ext = (this._mimeType || "").includes("mp4") ? "mp4" : "webm"
        const fd = new FormData()
        fd.append("audio", blob, `voice.${ext}`)
        const r = await fetch(this._url, {
            method: "POST",
            headers: { ...(token ? { "X-CSRF-Token": token } : {}) },
            body: fd,
            credentials: "same-origin"
        })
        const data = await r.json()
        if (!data || !data.ok) throw new Error(data?.error || "failed")
        return data.text
    }

    _pickMime() {
        if (!window.MediaRecorder) return ""
        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus"
        if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm"
        if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4"
        return ""
    }

    _teardownStream() {
        try {
            if (this._stream) this._stream.getTracks().forEach((t) => t.stop())
        } catch (_e) {}
        this._stream = null
        this._recorder = null
    }

    _fail(message) {
        this._setState("idle")
        this._onError(message)
    }

    _setState(next) {
        this.state = next
        this._onState(next)
    }
}
